import { db } from '../../config/firebaseconfig'
import requestGroupUAUpdate from './requestGroupUAUpdate'

/**
 * Deletes all of the db records associated with a previous upload action
 * @param {object} backup a record of an upload action from legacyUploadBackups in db
 * @param {function} updateState 
 * @param {string} district id of the district the school belongs to
 */
export default function unwindBackup (backup, updateState, district) {
  // Updates the state with an error
  function logError (id, recordType, updateState, err) {
    console.log(err)
    updateState(({unwindErrors}) => {
      const msg = `Error deleting ${recordType} ${id}.`
      return {unwindErrors: unwindErrors.concat([msg])}
    })
  }

  // Updates the state each time a db call succeeds.
  // Allows for count of deleted records for progress updates.
  function logSuccess(statusKey, updateState) {
    updateState(state => {
      return {[statusKey]: state[statusKey] + 1}
    })
  }

  // Creates an array of delete request promises when given
  // an array of ids and info on the records.
  function createDeleteRequests (
    ids, 
    collectionName, 
    statusName, 
    recordType, 
    updateState
  ) {
    return ids.map(id => {
      return db.collection(collectionName).doc(id).delete()
        .then(() => logSuccess(statusName, updateState))
        .catch(err => logError(id, recordType, updateState, err))
    })
  }
  
  const { 
    assessmentId,
    createdClasses,
    createdStudents,
    createdTeachers,
    createdUserAssessments,
    id,
    recordId,
    schoolYear,
  } = backup

  // Delete all classes created by the upload
  const deleteClasses = createDeleteRequests(
    createdClasses,
    'classes',
    'statusClasses',
    'class',
    updateState
  )
  updateState({
    status: 'Deleting classes...', 
    unwindPhase: 'Classes',
    backupUnwinding: id,
  })
  return Promise.all(deleteClasses)
    .then(() => {
      // Delete all students created by the upload
      updateState({
        status: 'Deleting students...', 
        unwindPhase: 'Students'
      })
      const deleteStudents = createDeleteRequests(
        createdStudents,
        'students',
        'statusStudents',
        'student',
        updateState
      )
      return Promise.all(deleteStudents)
    })
    .then(() => {
      // Delete all teachers created by the upload
      updateState({
        status: 'Deleting teachers...', 
        unwindPhase: 'Teachers'
      })
      const deleteTeachers = createDeleteRequests(
        createdTeachers,
        'educators',
        'statusTeachers',
        'teacher',
        updateState
      )
      return Promise.all(deleteTeachers)
    })
    .then(() => {
      // Delete all userAssessments created by the upload
      updateState({
        status: 'Deleting userAssessments...', 
        unwindPhase: 'UserAssessments',
      })
      const deleteUserAssessments = createDeleteRequests(
        createdUserAssessments,
        'userAssessments',
        'statusUserAssessments',
        'userAssessment',
        updateState
      )
      return Promise.all(deleteUserAssessments)
    })
    .then(() => {
      // Delete all groupUAs created by the upload
      updateState({
        status: 'Deleting groupUAs',
        unwindPhase: 'GroupUAs',
      })
      const gUAs = createdTeachers.concat(createdClasses)
      const deleteGroupUAs = createDeleteRequests(
        gUAs,
        'groupUA',
        'statusGroupUAs',
        'groupUA',
        updateState
      )
      return Promise.all(deleteGroupUAs)
    })
    .then(() => {
      // Set the backup record to inactive
      return db.collection('legacyUploadBackups').doc(id)
        .update({active: false})
    })
    .then(() => {
      // Update the groupUAs for the school and district
      updateState({status: 'Updating groupUA reports...'})
      const updateSchool = requestGroupUAUpdate(recordId, assessmentId, schoolYear)
      const updateDistrict = requestGroupUAUpdate(district, assessmentId, schoolYear)
      return Promise.all([updateSchool, updateDistrict])
    })
    .then(() => {
      // And we're done!
      updateState({
        status: `Unwind complete! Refresh your browser to take 
                more actions on this page.`,
      })
    })
    .catch(err => {
      console.log(err)
      updateState(state => {
        return {unwindErrors: state.unwindErrors.concat['Error unwinding.']}
      })
    })
  
}


// WEBPACK FOOTER //
// ./src/helpers/assessment/unwindBackup.js